/**
 * AckDialog (COURT-FORMS-BUILD, D-134.6) — the acknowledgment gate shown before a fact-only DRAFT
 * court form downloads. The executor must confirm they understand a lawyer / licensed paralegal has
 * to review the form before filing. Copy is the single source in gov-form-framing.ts. Self-contained
 * lightweight modal (matches GovFormZone's inline-token styling; no Radix dependency → trivially
 * unit-testable). The confirm button is disabled until the checkbox is ticked.
 */
import { useState } from "react";
import { DRAFT_ACK_COPY } from "@shared/constants/gov-form-framing";
import { SAGE, WARM } from "@shared/brand";


export function AckDialog({
  open,
  busy = false,
  error = null,
  advisory = null,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  busy?: boolean;
  error?: string | null; // GOVFORM-ERROR-UX (s138): a failed generate keeps the dialog open + shows this
  /** BENEFICIARY-FILL-1 (s156, ruling 2): an optional per-form advisory line (e.g. 74B service). */
  advisory?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [checked, setChecked] = useState(false);
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={DRAFT_ACK_COPY.heading}
      data-testid="ack-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(43,42,38,0.45)" }}
    >
      <div className="w-full max-w-md rounded-lg p-5" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}>
        <h2 className="text-[15px] font-semibold" style={{ color: WARM.charcoal }}>{DRAFT_ACK_COPY.heading}</h2>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: WARM.taupe }}>{DRAFT_ACK_COPY.body}</p>
        {advisory && (
          <p
            className="mt-2 text-[13px] leading-relaxed font-medium"
            style={{ color: WARM.charcoal }}
            data-testid="ack-dialog-advisory"
          >
            {advisory}
          </p>
        )}

        <label className="mt-4 flex items-start gap-2 text-[13px] cursor-pointer" style={{ color: WARM.charcoal }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="ack-dialog-checkbox"
            className="mt-0.5"
          />
          <span>{DRAFT_ACK_COPY.checkbox}</span>
        </label>

        {error && (
          <p className="mt-3 text-[12px] text-destructive" role="alert" data-testid="ack-dialog-error">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="ack-dialog-cancel"
            className="rounded-md px-3 py-1.5 text-[13px] font-medium"
            style={{ color: WARM.charcoal, border: `1px solid ${WARM.stone}` }}
          >
            {DRAFT_ACK_COPY.cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!checked || busy}
            data-testid="ack-dialog-confirm"
            className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: SAGE[600] }}
          >
            {busy ? "Preparing…" : DRAFT_ACK_COPY.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
